// src/components/ForecastIntel.jsx
// The redesigned Forecasts "All" view, three panels over one shared fetch:
//   1. Funding outlook — money items likely to return, from recurrence
//      patterns and stated schedules in the record.
//   2. Pushback forecast — subjects where board division is likely to recur,
//      from where divided votes actually cluster.
//   3. Test a proposal — type a measure; it is scored against the record with
//      every factor shown. Deterministic keyword matching, no model call.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { fetchAllRows } from '../utils/fetchAllRows';
import {
  buildSignals, pushbackForecast, scoreProposal, fmtMoney, EXAMPLE_PROPOSALS,
} from './forecastSignals';
import './ForecastIntel.css';

const fmtDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y) return String(iso);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
};

/* ── 1. Funding outlook ── */
function FundingOutlook({ signals }) {
  if (!signals.funding.length) return null;
  return (
    <div className="fi-card">
      <h3 className="fi-title">Funding outlook — money likely to come back around</h3>
      <p className="fi-sub">
        Funding streams in the record that recur on a cycle or have a
        stated next step, with the largest amount the record mentions.
      </p>
      <ul className="fi-fund-list">
        {signals.funding.map((f) => (
          <li key={f.id}>
            <div className="fi-fund-main">
              <span className="fi-fund-label">{f.label}</span>
              <span className="fi-fund-amt">
                {f.maxAmount ? fmtMoney(f.maxAmount) : '—'}
              </span>
            </div>
            <div className="fi-fund-meta">
              {f.n} item{f.n === 1 ? '' : 's'} in the record · last {fmtDate(f.lastDate)} · {f.cadence}
            </div>
          </li>
        ))}
      </ul>
      <p className="fi-note">
        Derived from recurrence patterns and schedules stated in the record — not
        a budget document, and amounts are the largest single figure mentioned,
        not totals.
      </p>
    </div>
  );
}

/* ── 2. Pushback forecast ── */
function PushbackForecast({ signals }) {
  const navigate = useNavigate();
  const rows = useMemo(() => pushbackForecast(signals), [signals]);
  if (!rows.length) return null;
  const coveredSplits = new Set(rows.flatMap((r) => r.splits.map((s) => s.id))).size;
  return (
    <div className="fi-card">
      <h3 className="fi-title">Pushback forecast — where division is likely next</h3>
      <p className="fi-sub">
        Subjects where this board has already split, ranked by how close those
        votes were. If these topics return, expect the same fault lines.
      </p>
      <div className="fi-push-list">
        {rows.map((r) => (
          <div className="fi-push-row" key={r.cluster.id}>
            <div className="fi-push-head">
              <span className={`fi-heat fi-heat-${r.heat.replace(/\s/g, '').toLowerCase()}`}>
                {r.heat}
              </span>
              <span className="fi-push-label">{r.cluster.label}</span>
              <span className="fi-push-n">
                {r.n} split vote{r.n === 1 ? '' : 's'} · closest {r.splits[0].tally.yes}–{r.splits[0].tally.no}
              </span>
            </div>
            <div className="fi-push-detail">
              {r.dissenters.length > 0 && (
                <span>
                  Recurring no votes:{' '}
                  {r.dissenters.slice(0, 4).map(([n, c]) => `${n} (${c})`).join(', ')}
                </span>
              )}
              <button
                type="button"
                className="fi-link"
                onClick={() => navigate(`?tab=voting#vote-${r.splits[0].id}`)}
              >
                closest vote →
              </button>
            </div>
          </div>
        ))}
      </div>
      <p className="fi-note">
        {coveredSplits} of {signals.divided.length} divided votes in the record fall in
        these subjects. Projection assumes the pattern holds; a new measure can
        always break it.
      </p>
    </div>
  );
}

/* ── 3. Test a proposal ── */
function PassEstimator({ signals }) {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [showHow, setShowHow] = useState(false);

  const result = useMemo(
    () => (submitted ? scoreProposal(submitted, signals) : null),
    [submitted, signals]
  );

  const run = (text) => {
    setInput(text);
    setSubmitted(text.trim());
  };

  return (
    <div className="fi-card">
      <h3 className="fi-title">Test a proposal — would it pass?</h3>
      <p className="fi-sub">
        Describe a measure in a sentence. It is matched against this
        board&rsquo;s votes, debates, public comment and meeting time, and scored
        with every factor shown. Use concrete subject words — the matching is
        by keyword, not AI.
      </p>

      <form
        className="fi-ask"
        onSubmit={(e) => { e.preventDefault(); setSubmitted(input.trim()); }}
      >
        <input
          type="text"
          className="fi-input"
          value={input}
          placeholder="e.g. Expand cannabis cafe permits to more neighborhoods"
          aria-label="Describe a proposal to score"
          onChange={(e) => setInput(e.target.value)}
          maxLength={300}
        />
        <button type="submit" className="fi-go" disabled={!input.trim()}>
          Score it
        </button>
      </form>

      <div className="fi-examples">
        <span className="fi-examples-label">Try:</span>
        {EXAMPLE_PROPOSALS.map((ex) => (
          <button key={ex.label} type="button" className="fi-example-chip" onClick={() => run(ex.text)}>
            {ex.label}
          </button>
        ))}
      </div>

      {result && !result.ok && (
        <div className="fi-noscore">
          {result.reason === 'defiance'
            ? 'This reads as a measure to defy or evade a legal requirement — the board does not pass those, and the record has nothing to score it against.'
            : 'Not enough matching history to score this — nothing in the record resembles it. Try more concrete subject words (a topic, a program, a place); a score without evidence would be a guess.'}
        </div>
      )}

      {result && result.ok && (
        <div className="fi-result">
          <div className="fi-score-row">
            <div className={`fi-score fi-band-${result.band.id}`}>
              <span className="fi-score-num">{result.combined}</span>
              <span className="fi-score-of">/100</span>
            </div>
            <div className="fi-score-text">
              <div className="fi-band-label">{result.band.label}</div>
              <div className="fi-score-meta">
                Filed under {result.catLabel} · evidence confidence: {result.confidence}
              </div>
            </div>
          </div>

          <div className="fi-subscores">
            <div className="fi-subscore">
              <span className="fi-subscore-val">{Math.round(result.A * 100)}%</span>
              <span className="fi-subscore-lbl">
                reaches a vote
                <span className="fi-subscore-hint"> — scheduled and pursued</span>
              </span>
            </div>
            <div className="fi-subscore">
              {/* Never print 100% for a future vote of real officials */}
              <span className="fi-subscore-val">{Math.min(99, Math.round(result.B * 100))}%</span>
              <span className="fi-subscore-lbl">
                passes if voted
                {result.showMembers && (
                  <span className="fi-subscore-hint"> — likely roll call ≈ {result.predictedSplit.yes}–{result.predictedSplit.no}</span>
                )}
              </span>
            </div>
          </div>

          {result.likelyNo.length > 0 && result.showMembers && (
            <p className="fi-likely-no">
              <strong>Likely no votes:</strong>{' '}
              {result.likelyNo.map((m) => `${m.name} (${Math.round(m.p * 100)}%)`).join(', ')}
              <span className="fi-likely-no-src">
                {' '}— from each member&rsquo;s own roll calls across {result.matchedRollCount} matched vote{result.matchedRollCount === 1 ? '' : 's'}, unanimous ones included
              </span>
            </p>
          )}
          {!result.showMembers && result.confidence === 'Low' && (
            <p className="fi-likely-no-suppressed">
              Named members and a predicted split are not shown at low
              confidence — the input is too thin to attach anyone&rsquo;s record to.
            </p>
          )}

          <div className="fi-factors">
            {[...result.aFactors, ...result.bFactors].map((f) => (
              <div className={`fi-factor fi-factor-${f.direction}`} key={f.id}>
                <span className="fi-factor-arrow">{f.direction === 'up' ? '▲' : '▼'}</span>
                <span className="fi-factor-body">
                  <span className="fi-factor-label">{f.label}</span>
                  <span className="fi-factor-detail">{f.detail}</span>
                </span>
              </div>
            ))}
          </div>

          {result.suggestions && result.suggestions.length > 0 && (
            <div className="fi-suggest">
              <h4>If you pursued this — patterns from prior amendments</h4>
              <ul>
                {result.suggestions.map((s) => <li key={s}>{s}</li>)}
              </ul>
            </div>
          )}

          {(result.evidence.dividedVotes.length > 0 || result.evidence.nearDup) && (
            <div className="fi-evidence">
              <h4>The record it matched</h4>
              <ul>
                {result.evidence.nearDup && (
                  <li key="dup">
                    <button type="button" className="fi-link" onClick={() => navigate(`?tab=voting#vote-${result.evidence.nearDup.id}`)}>
                      {fmtDate(result.evidence.nearDup.date)} · passed {result.evidence.nearDup.tally.yes}–{result.evidence.nearDup.tally.no}
                    </button>{' '}
                    {result.evidence.nearDup.issue}
                  </li>
                )}
                {result.evidence.dividedVotes.map((d) => (
                  <li key={d.id}>
                    <button type="button" className="fi-link" onClick={() => navigate(`?tab=voting#vote-${d.id}`)}>
                      {fmtDate(d.date)} · {d.tally.yes}–{d.tally.no}
                    </button>{' '}
                    {d.issue}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <button type="button" className="fi-how-toggle" aria-expanded={showHow} onClick={() => setShowHow(!showHow)}>
            {showHow ? 'Hide how this score works' : 'How this score works'}
          </button>
          {showHow && (
            <div className="fi-how">
              <p>
                Two probabilities are estimated separately and multiplied. Nearly
                every measure that reaches a roll call here passes ({signals.passedBills.length} of{' '}
                {signals.recorded.length} in the record), so the interesting risks are
                never being scheduled, and passing only after a fight.
              </p>
              <p>
                <strong>Reaches a vote</strong> starts at 60% and moves on: a
                near-identical measure recently passed (−35), the board actively
                legislating the subject (+10), the category&rsquo;s share of meeting
                time (+15/+8/−10), legal-compliance framing (+15), overlap with a
                trending topic (+8), and new spending in a deficit climate (−5).
                Public-comment activity is shown but carries no weight — a
                commenter&rsquo;s recorded stance is toward the item they spoke on,
                which may not map to this proposal&rsquo;s direction.
              </p>
              <p>
                <strong>Passes if voted</strong> models each supervisor&rsquo;s
                chance of voting no from their record on similar divided votes,
                blended with their overall no rate when history is thin, then asks
                how likely the no count stays under six. Division here is
                bloc-like, so the uncertainty is widened rather than treating
                members as independent.
              </p>
              <p>
                Matching is keyword overlap plus known contested-topic families —
                it cannot read intent, and a measure with no similar history gets
                the board&rsquo;s (very high) base rate with low confidence.
                &ldquo;Passes if voted&rdquo; runs high for nearly everything
                because floor defeats are genuinely rare here — the information
                is mostly in the agenda odds and the expected split, not in
                whether B is 96% or 99%.
              </p>
            </div>
          )}

          <p className="fi-note">
            Member percentages summarize each member&rsquo;s past public roll-call
            votes on similar matters — they describe the record, not an
            assertion of how anyone will vote. The score itself is built from
            fixed, hand-set weights and has never been validated against
            outcomes: read it as a ranking of likelihood, not a calibrated
            probability. New facts, sponsors, or amendments can change
            everything.
          </p>
        </div>
      )}
    </div>
  );
}

/* ── Parent: one fetch, three panels ── */
function ForecastIntel({ districtId }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const [v, m, c, t] = await Promise.all([
          fetchAllRows(() => supabase.from('Votings').select('id, voteDate, category, issue, outcome, votes')
            .eq('districtId', districtId)
            .order('voteDate', { ascending: false }).order('id', { ascending: false })),
          supabase.from('Meetings').select('id, date, json_data')
            .eq('districtId', districtId).order('date', { ascending: false }).limit(200),
          fetchAllRows(() => supabase.from('public_comments').select('themes, stance')
            .eq('districtId', districtId).order('id')),
          supabase.from('Topics').select('title').eq('districtId', districtId)
            .eq('current', true).limit(50),
        ]);
        if (cancelled) return;
        // A failed core query is a load error, not an empty record — reporting
        // "not enough votes" on an RLS/network failure would be a false claim
        // about the district.
        if (v.error || m.error) {
          setLoadError(true);
        } else {
          setData({
            votings: v.data || [],
            meetings: m.data || [],
            comments: c.error ? [] : c.data || [],
            topics: t.error ? [] : t.data || [],
          });
        }
      } catch {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [districtId]);

  const signals = useMemo(() => (data ? buildSignals(data) : null), [data]);

  if (loading) return <div className="fi-card fi-status">Reading the record…</div>;
  if (loadError) {
    return (
      <div className="fi-card fi-status">
        Couldn&rsquo;t load the voting record just now — try refreshing.
      </div>
    );
  }
  if (!signals || !signals.recorded.length) {
    return (
      <div className="fi-card fi-status">
        Not enough recorded votes yet to build an outlook for this district.
      </div>
    );
  }

  return (
    <div className="fi-wrap">
      <p className="fi-fresh">
        Built live from the record through <strong>{fmtDate(signals.latestDate)}</strong> —
        every panel here recomputes automatically as new meetings are processed.
      </p>
      <FundingOutlook signals={signals} />
      <PushbackForecast signals={signals} />
      <PassEstimator signals={signals} />
    </div>
  );
}

export default ForecastIntel;
